// Brand identity for the entries in the AI magic wand menus.
//
// `provider` on an AiModel is the API gateway the request is routed through
// (fal, replicate, vertex, ...), not the lab that made the model, so the brand
// is resolved from the action tag first, then the model's names, and only then
// from the provider. Keep this module free of runtime imports so the resolver
// stays cheap to unit test.
//
// Mark geometry comes from simple-icons (CC0) and @lobehub/icons (MIT). The
// logos themselves remain trademarks of their owners and are used only to
// identify the lab whose model an entry runs.

export type AiModelBrandId =
    | 'treechat'
    | 'openai'
    | 'google'
    | 'gemini'
    | 'xai'
    | 'bytedance'
    | 'kling'
    | 'minimax'
    | 'bfl'
    | 'stability'
    | 'elevenlabs'
    | 'suno'
    | 'alibaba'
    | 'qwen'
    | 'luma'
    | 'midjourney'
    | 'runway'
    | 'resemble'
    | 'mmaudio'
    | 'anthropic'

export interface AiModelBrandMarkPath {
    d: string
    // 'gradient' fills the path with the mark's gradient; omitted paths use the tile foreground.
    fill?: string | 'gradient'
    fillOpacity?: number
    fillRule?: 'evenodd' | 'nonzero'
}

export interface AiModelBrandMarkGradient {
    // Unit-square coordinates of the gradient vector.
    x1: number
    y1: number
    x2: number
    y2: number
    stops: Array<{ offset: number; color: string }>
}

export interface AiModelBrandMark {
    viewBox: string
    paths: AiModelBrandMarkPath[]
    gradient?: AiModelBrandMarkGradient
    // Fraction of the avatar the mark should span; marks that already carry padding go larger.
    scale?: number
}

export interface AiModelBrandTile {
    background: string
    foreground: string
}

// A hue the chooser washes at low alpha behind the tile so each entry gets a seat in its own colour.
export type AiModelBrandHalo = string

export interface AiModelBrand {
    id: AiModelBrandId
    name: string
    halo: AiModelBrandHalo
    tile: AiModelBrandTile
    mark?: AiModelBrandMark
    // Letters shown when the brand has no mark we can ship.
    monogram?: string
}

export interface AiModelBrandSubject {
    actionTagName?: string | null
    commandTag?: string | null
    humanName?: string | null
    name?: string | null
    provider?: string | null
    kind?: string | null
    toolKey?: string | null
}

const DARK_TILE = '#0b0b0f'
const LIGHT_TILE = '#ffffff'

const TREECHAT_TREE_PATH =
    'M255.932 86.4619C264.865 86.4619 272.107 93.7044 272.107 102.638V141.61C272.107 144.342 275.033 146.075 277.429 144.763L318.705 122.152C326.54 117.86 336.37 120.733 340.662 128.568C344.954 136.404 342.082 146.235 334.247 150.527L278.005 181.336C274.368 183.328 272.107 187.145 272.107 191.292V332.233C272.107 336.612 276.857 339.342 280.64 337.137L297.345 327.401C300.834 325.368 302.98 321.633 302.98 317.594V250.727C302.98 229.595 314.223 210.059 332.494 199.442L376.761 176.558C384.486 172.069 394.386 174.693 398.874 182.418C403.362 190.142 400.739 200.043 393.014 204.531L348.747 227.416C340.442 232.242 335.332 241.122 335.332 250.727V299.007C335.332 301.781 338.34 303.51 340.736 302.113L378.091 280.343C385.81 275.845 395.713 278.456 400.211 286.175C404.71 293.894 402.099 303.798 394.38 308.296L279.034 378.195C274.734 380.801 272.107 385.464 272.107 390.493V409.944C272.107 418.878 264.865 426.12 255.932 426.12C246.998 426.12 239.756 418.878 239.756 409.944V390.523C239.756 385.487 237.121 380.818 232.81 378.215L117.055 308.308C109.33 303.821 106.704 293.921 111.19 286.196C115.677 278.47 125.577 275.844 133.302 280.331L170.914 302.175C173.31 303.567 176.314 301.838 176.314 299.067V250.771C176.314 241.151 171.188 232.26 162.863 227.439L118.421 204.544C110.69 200.067 108.051 190.171 112.528 182.439C117.004 174.707 126.9 172.069 134.631 176.545L179.073 199.441C197.388 210.046 208.665 229.607 208.665 250.771V317.567C208.665 321.612 210.817 325.351 214.315 327.382L231.231 337.207C235.014 339.404 239.756 336.674 239.756 332.299V191.241C239.756 187.106 237.507 183.297 233.885 181.301L178.028 150.506C170.204 146.193 167.358 136.354 171.671 128.53C175.984 120.706 185.823 117.86 193.646 122.173L234.426 144.655C236.822 145.976 239.756 144.243 239.756 141.507V102.638C239.756 93.7044 246.998 86.4619 255.932 86.4619Z'

export const AI_MODEL_BRANDS: Record<AiModelBrandId, AiModelBrand> = {
    treechat: {
        id: 'treechat',
        name: 'Treechat',
        halo: '#8d7fe3',
        tile: { background: '#8d7fe3', foreground: '#ffffff' },
        mark: {
            viewBox: '0 0 512 512',
            scale: 0.92,
            paths: [{ d: TREECHAT_TREE_PATH }],
        },
    },
    openai: {
        id: 'openai',
        name: 'OpenAI',
        halo: '#6b7280',
        tile: { background: LIGHT_TILE, foreground: '#000000' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.6,
            paths: [
                {
                    d: 'M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z',
                },
            ],
        },
    },
    google: {
        id: 'google',
        name: 'Google',
        halo: '#4285f4',
        tile: { background: LIGHT_TILE, foreground: '#4285f4' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.6,
            paths: [
                {
                    d: 'M23 12.245c0-.905-.075-1.565-.236-2.25h-10.54v4.083h6.186c-.124 1.014-.797 2.542-2.294 3.569l-.021.136 3.332 2.53.23.022C21.779 18.417 23 15.593 23 12.245z',
                    fill: '#4285f4',
                },
                {
                    d: 'M12.225 23c3.03 0 5.574-.978 7.433-2.665l-3.542-2.688c-.948.648-2.22 1.1-3.891 1.1a6.745 6.745 0 01-6.386-4.572l-.132.011-3.465 2.628-.045.124C4.043 20.531 7.835 23 12.225 23z',
                    fill: '#34a853',
                },
                {
                    d: 'M5.84 14.175A6.65 6.65 0 015.463 12c0-.758.138-1.491.361-2.175l-.006-.147-3.508-2.67-.115.054A10.831 10.831 0 001 12c0 1.772.436 3.447 1.197 4.938l3.642-2.763z',
                    fill: '#fbbc05',
                },
                {
                    d: 'M12.225 5.253c2.108 0 3.529.892 4.34 1.638l3.167-3.031C17.787 2.088 15.255 1 12.225 1 7.834 1 4.043 3.469 2.197 7.062l3.63 2.763a6.77 6.77 0 016.398-4.572z',
                    fill: '#eb4335',
                },
            ],
        },
    },
    gemini: {
        id: 'gemini',
        name: 'Google Gemini',
        halo: '#4c6dff',
        tile: { background: LIGHT_TILE, foreground: '#3186ff' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.66,
            gradient: {
                x1: 0,
                y1: 1,
                x2: 1,
                y2: 0,
                stops: [
                    { offset: 0, color: '#1c69ff' },
                    { offset: 0.55, color: '#7a6cff' },
                    { offset: 1, color: '#d55dff' },
                ],
            },
            paths: [
                {
                    d: 'M11.04 19.32Q12 21.51 12 24q0-2.49.93-4.68.96-2.19 2.58-3.81t3.81-2.55Q21.51 12 24 12q-2.49 0-4.68-.93a12.3 12.3 0 0 1-3.81-2.58 12.3 12.3 0 0 1-2.58-3.81Q12 2.49 12 0q0 2.49-.96 4.68-.93 2.19-2.55 3.81a12.3 12.3 0 0 1-3.81 2.58Q2.49 12 0 12q2.49 0 4.68.96 2.19.93 3.81 2.55t2.55 3.81',
                    fill: 'gradient',
                },
            ],
        },
    },
    xai: {
        id: 'xai',
        name: 'xAI',
        halo: '#6b7280',
        tile: { background: DARK_TILE, foreground: '#ffffff' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.58,
            paths: [
                {
                    d: 'M6.469 8.776L16.512 23h-4.464L2.005 8.776H6.47zm-.004 7.9l2.233 3.164L6.467 23H2l4.465-6.324zM22 2.582V23h-3.659V7.764L22 2.582zM22 1l-9.952 14.095-2.233-3.163L17.533 1H22z',
                    fillRule: 'evenodd',
                },
            ],
        },
    },
    bytedance: {
        id: 'bytedance',
        name: 'ByteDance',
        halo: '#3c8cff',
        tile: { background: LIGHT_TILE, foreground: '#325ab4' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.62,
            paths: [
                {
                    d: 'M14.944 18.587l-1.704-.445V10.01l1.824-.462c1-.254 1.84-.461 1.88-.453.032 0 .056 2.235.056 4.972v4.973l-.176-.008c-.104 0-.952-.207-1.88-.446z',
                    fill: '#00c8d2',
                },
                {
                    d: 'M7 16.542c0-2.736.024-4.98.064-4.98.032-.008.872.2 1.88.454l1.816.461-.016 4.05-.024 4.049-1.632.422c-.896.23-1.736.445-1.856.469L7 21.523v-4.98z',
                    fill: '#3c8cff',
                },
                {
                    d: 'M19.24 12.477c0-9.03.008-9.515.144-9.475.072.024.784.207 1.576.406.792.207 1.576.405 1.744.445l.296.08-.016 8.56-.024 8.568-1.624.414c-.888.23-1.728.437-1.856.47l-.24.055v-9.523z',
                    fill: '#78e6dc',
                },
                {
                    d: 'M1 12.509c0-4.678.024-8.505.064-8.505.032 0 .872.207 1.872.454l1.824.461v7.582c0 4.16-.016 7.574-.032 7.574-.024 0-.872.215-1.88.47L1 21.013v-8.505z',
                    fill: '#325ab4',
                },
            ],
        },
    },
    kling: {
        id: 'kling',
        name: 'Kling AI',
        halo: '#0bd2e0',
        tile: { background: DARK_TILE, foreground: '#0bf2f9' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.66,
            gradient: {
                x1: 0,
                y1: 0,
                x2: 1,
                y2: 1,
                stops: [
                    { offset: 0, color: '#fff959' },
                    { offset: 0.42, color: '#0df35e' },
                    { offset: 0.72, color: '#0bf2f9' },
                    { offset: 1, color: '#04a6f0' },
                ],
            },
            paths: [
                {
                    d: 'M5.493 21.234c-1.112-1.451-1.109-4.263-.081-7.459l-4.557-2.63a1.683 1.683 0 01-.85-1.304 1.505 1.505 0 01.08-.622 13.18 13.18 0 011.037-2.255c3.476-6.02 10.916-8.23 16.619-4.938.46.266.82.67 1.081 1.184.785 1.545.685 4.096-.234 6.954l4.557 2.631c.339.196.596.492.736.832a1.53 1.53 0 01.034 1.093 13.146 13.146 0 01-1.037 2.255c-3.476 6.02-10.916 8.23-16.619 4.938a2.6 2.6 0 01-.766-.68zm11.096-6.615c-2.073 3.591-5.808 5.316-8.343 3.852-1.267-.731-1.994-2.122-2.145-3.778-.095-1.035.036-2.173.4-3.32.217-.684.517-1.37.902-2.039l.008-.014c2.073-3.59 5.808-5.315 8.343-3.852.633.366 1.13.895 1.49 1.54.986 1.772.922 4.415-.285 6.914-.111.23-.232.457-.362.683l-.008.014z',
                    fill: 'gradient',
                    fillRule: 'evenodd',
                },
            ],
        },
    },
    minimax: {
        id: 'minimax',
        name: 'MiniMax',
        halo: '#f0384f',
        tile: { background: LIGHT_TILE, foreground: '#f0384f' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.66,
            gradient: {
                x1: 0,
                y1: 0.5,
                x2: 1,
                y2: 0.5,
                stops: [
                    { offset: 0, color: '#e2167e' },
                    { offset: 1, color: '#fe603c' },
                ],
            },
            paths: [
                {
                    d: 'M16.278 2c1.156 0 2.093.927 2.093 2.07v12.501a.74.74 0 00.744.709.74.74 0 00.743-.709V9.099a2.06 2.06 0 012.071-2.049A2.06 2.06 0 0124 9.1v6.561a.649.649 0 01-.652.645.649.649 0 01-.653-.645V9.1a.762.762 0 00-.766-.758.762.762 0 00-.766.758v7.472a2.037 2.037 0 01-2.048 2.026 2.037 2.037 0 01-2.048-2.026v-12.5a.785.785 0 00-.788-.753.785.785 0 00-.789.752l-.001 15.904A2.037 2.037 0 0113.441 22a2.037 2.037 0 01-2.048-2.026V18.04c0-.356.292-.645.652-.645.36 0 .652.289.652.645v1.934c0 .263.142.506.372.638.23.131.514.131.744 0a.734.734 0 00.372-.638V4.07c0-1.143.937-2.07 2.093-2.07zm-5.674 0c1.156 0 2.093.927 2.093 2.07v11.523a.648.648 0 01-.652.645.648.648 0 01-.652-.645V4.07a.785.785 0 00-.789-.78.785.785 0 00-.789.78v14.013a2.06 2.06 0 01-2.07 2.048 2.06 2.06 0 01-2.071-2.048V9.1a.762.762 0 00-.766-.758.762.762 0 00-.766.758v3.8a2.06 2.06 0 01-2.071 2.049A2.06 2.06 0 010 12.9v-1.378c0-.357.292-.646.652-.646.36 0 .653.29.653.646V12.9c0 .418.343.757.766.757s.766-.339.766-.757V9.099a2.06 2.06 0 012.07-2.048 2.06 2.06 0 012.071 2.048v8.984c0 .419.343.758.767.758.423 0 .766-.339.766-.758V4.07c0-1.143.937-2.07 2.093-2.07z',
                    fill: 'gradient',
                    fillRule: 'nonzero',
                },
            ],
        },
    },
    bfl: {
        id: 'bfl',
        name: 'Black Forest Labs',
        halo: '#6b7280',
        tile: { background: DARK_TILE, foreground: '#ffffff' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.64,
            paths: [
                {
                    d: 'M0 20.683L12.01 2.5 24 20.683h-2.233L12.009 5.878 3.471 18.806h12.122l1.239 1.877H0z',
                },
                {
                    d: 'M8.069 16.724l2.073-3.115 2.074 3.115H8.069zM18.24 20.683l-5.668-8.707h2.177l5.686 8.707h-2.196zM19.74 11.676l2.13-3.19 2.13 3.19h-4.26z',
                },
            ],
        },
    },
    stability: {
        id: 'stability',
        name: 'Stability AI',
        halo: '#8b5cf6',
        tile: {
            background: 'linear-gradient(135deg, #6a3df5 0%, #b44cff 100%)',
            foreground: '#ffffff',
        },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.6,
            paths: [
                {
                    d: 'M7.223 21c4.252 0 7.018-2.22 7.018-5.56 0-2.59-1.682-4.236-4.69-4.918l-1.93-.571c-1.694-.375-2.683-.825-2.45-1.975.194-.957.773-1.497 2.122-1.497 4.285 0 5.873 1.497 5.873 1.497v-3.6S11.62 3 7.293 3C3.213 3 1 5.07 1 8.273c0 2.59 1.534 4.097 4.645 4.812l.334.083c.473.144 1.112.335 1.916.572 1.59.375 1.999.773 1.999 1.966 0 1.09-1.15 1.71-2.67 1.71C2.841 17.416 1 15.231 1 15.231v3.989S2.152 21 7.223 21z',
                },
                {
                    d: 'M20.374 20.73c1.505 0 2.626-1.073 2.626-2.526 0-1.484-1.089-2.526-2.626-2.526-1.505 0-2.594 1.042-2.594 2.526 0 1.484 1.089 2.526 2.594 2.526z',
                },
            ],
        },
    },
    elevenlabs: {
        id: 'elevenlabs',
        name: 'ElevenLabs',
        halo: '#6b7280',
        tile: { background: LIGHT_TILE, foreground: '#000000' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.48,
            paths: [{ d: 'M4.6035 0v24h4.9317V0zm9.8613 0v24h4.9317V0z' }],
        },
    },
    suno: {
        id: 'suno',
        name: 'Suno',
        halo: '#6b7280',
        tile: { background: DARK_TILE, foreground: '#ffffff' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.54,
            paths: [
                {
                    d: 'M16.5 0C20.642 0 24 5.373 24 12h-9c0 6.627-3.358 12-7.5 12C3.358 24 0 18.627 0 12h9c0-6.627 3.358-12 7.5-12Z',
                },
            ],
        },
    },
    alibaba: {
        id: 'alibaba',
        name: 'Alibaba',
        halo: '#ff6a00',
        tile: { background: '#ff6a00', foreground: '#ffffff' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.66,
            paths: [
                {
                    d: 'M3.996 4.517h5.291L8.01 6.324 4.153 7.506a1.668 1.668 0 0 0-1.165 1.601v5.786a1.668 1.668 0 0 0 1.165 1.6l3.857 1.183 1.277 1.807H3.996A3.996 3.996 0 0 1 0 15.487V8.513a3.996 3.996 0 0 1 3.996-3.996m16.008 0h-5.291l1.277 1.807 3.857 1.182c.715.227 1.17.889 1.165 1.601v5.786a1.668 1.668 0 0 1-1.165 1.6l-3.857 1.183-1.277 1.807h5.291A3.996 3.996 0 0 0 24 15.487V8.513a3.996 3.996 0 0 0-3.996-3.996m-4.007 8.345H8.002v-1.804h7.995Z',
                },
            ],
        },
    },
    qwen: {
        id: 'qwen',
        name: 'Qwen',
        halo: '#6336e7',
        tile: { background: LIGHT_TILE, foreground: '#6336e7' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.64,
            paths: [
                {
                    d: 'M23.919 14.545 20.817 9.17l1.47-2.544a.56.56 0 0 0 0-.566l-1.633-2.83a.57.57 0 0 0-.49-.283h-6.207L12.487.402a.57.57 0 0 0-.49-.284H8.732a.56.56 0 0 0-.49.284L5.139 5.775h-2.94a.56.56 0 0 0-.49.284L.077 8.887a.56.56 0 0 0 0 .567L3.18 14.83l-1.47 2.545a.56.56 0 0 0 0 .566l1.634 2.83a.57.57 0 0 0 .49.283h6.205l1.47 2.545a.57.57 0 0 0 .49.284h3.266a.57.57 0 0 0 .49-.284l3.104-5.375h2.94a.57.57 0 0 0 .49-.283l1.634-2.828a.55.55 0 0 0-.004-.568M8.733.686l1.634 2.828-1.634 2.828H21.8L20.164 9.17H7.425L5.63 6.06Zm1.306 19.801-6.205-.002 1.634-2.83h3.265L2.201 6.344h3.267q3.182 5.517 6.367 11.032zm10.124-5.66L18.53 12l-6.532 11.315-1.634-2.83c2.129-3.673 4.25-7.351 6.373-11.028h3.592l3.102 5.374z',
                    fill: '#6336e7',
                },
            ],
        },
    },
    luma: {
        id: 'luma',
        name: 'Luma AI',
        halo: '#6d5dfc',
        tile: { background: LIGHT_TILE, foreground: '#1b1bff' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.6,
            gradient: {
                x1: 0,
                y1: 1,
                x2: 1,
                y2: 0,
                stops: [
                    { offset: 0, color: '#0000aa' },
                    { offset: 1, color: '#a78dff' },
                ],
            },
            paths: [
                { d: 'M2 5.999L12.392 0v24L2 18V5.999z', fill: 'gradient' },
                {
                    d: 'M12.392 24L2 18l10.392-6 10.393 6-10.393 6z',
                    fill: 'gradient',
                    fillOpacity: 0.72,
                },
            ],
        },
    },
    midjourney: {
        id: 'midjourney',
        name: 'Midjourney',
        halo: '#6b7280',
        tile: { background: LIGHT_TILE, foreground: '#000000' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.66,
            paths: [
                {
                    d: 'M22.369 17.676c-1.387 1.259-3.17 2.378-5.332 3.417.044.03.086.057.13.083l.018.01.019.012c.216.123.42.184.641.184.222 0 .426-.061.642-.184l.018-.011.019-.011c.14-.084.266-.178.492-.366l.178-.148c.279-.232.426-.342.625-.456.304-.174.612-.266.949-.266.337 0 .645.092.949.266l.023.014c.188.109.334.219.602.442l.178.148c.221.184.346.278.483.36l.028.017.018.01c.21.12.407.181.62.185h.022a.31.31 0 110 .618c-.337 0-.645-.092-.95-.266a3.137 3.137 0 01-.09-.054l-.022-.014-.022-.013-.02-.014a5.356 5.356 0 01-.49-.377l-.159-.132a3.836 3.836 0 00-.483-.36l-.027-.017-.019-.01a1.256 1.256 0 00-.641-.185c-.222 0-.426.061-.641.184l-.02.011-.018.011c-.14.084-.266.178-.492.366l-.158.132a5.125 5.125 0 01-.51.39l-.022.014-.022.014-.09.054a1.868 1.868 0 01-.95.266c-.337 0-.644-.092-.949-.266a3.137 3.137 0 01-.09-.054l-.022-.014-.022-.013-.026-.017a4.881 4.881 0 01-.425-.325.308.308 0 01-.12-.1l-.098-.081a3.836 3.836 0 00-.483-.36l-.027-.017-.019-.01a1.256 1.256 0 00-.641-.185c-.222 0-.426.061-.642.184l-.018.011-.019.011c-.14.084-.266.178-.492.366l-.158.132a5.125 5.125 0 01-.51.39l-.023.014-.022.014-.09.054A1.868 1.868 0 0112 22c-.337 0-.645-.092-.949-.266a3.137 3.137 0 01-.09-.054l-.022-.014-.022-.013-.021-.014a5.356 5.356 0 01-.49-.377l-.158-.132a3.836 3.836 0 00-.483-.36l-.028-.017-.018-.01a1.256 1.256 0 00-.642-.185c-.221 0-.425.061-.641.184l-.019.011-.018.011c-.141.084-.266.178-.492.366l-.158.132a5.125 5.125 0 01-.511.39l-.022.014-.022.014-.09.054a1.868 1.868 0 01-.986.264c-.746-.09-1.319-.38-1.89-.866l-.035-.03c-.047-.041-.118-.106-.192-.174l-.196-.181-.107-.1-.011-.01a1.531 1.531 0 00-.336-.253.313.313 0 00-.095-.03h-.005c-.119.022-.238.059-.361.11a.308.308 0 01-.077.061l-.008.005a.309.309 0 01-.126.034 5.66 5.66 0 00-.774.518l-.416.324-.055.043a6.542 6.542 0 01-.324.236c-.305.207-.552.315-.8.315a.31.31 0 01-.01-.618h.01c.09 0 .235-.062.438-.198l.04-.027c.077-.054.163-.117.27-.199l.385-.301.06-.047c.268-.206.506-.373.73-.505l-.633-1.21a.309.309 0 01.254-.451l20.287-1.305a.309.309 0 01.228.537zm-1.118.14L2.369 19.03l.423.809c.128-.045.256-.078.388-.1a.31.31 0 01.052-.005c.132 0 .26.032.386.093.153.073.294.179.483.35l.016.015.092.086.144.134.097.089c.065.06.125.114.16.144.485.418.948.658 1.554.736h.011a1.25 1.25 0 00.6-.172l.021-.011.019-.011.018-.011c.141-.084.266-.178.492-.366l.178-.148c.279-.232.426-.342.625-.456.305-.174.612-.266.95-.266.336 0 .644.092.948.266l.023.014c.188.109.335.219.603.442l.177.148c.222.184.346.278.484.36l.027.017.019.01c.215.124.42.185.641.185.222 0 .426-.061.641-.184l.019-.011.018-.011c.141-.084.267-.178.493-.366l.177-.148c.28-.232.427-.342.626-.456.304-.174.612-.266.949-.266.337 0 .644.092.949.266l.025.015c.187.109.334.22.603.443 1.867-.878 3.448-1.811 4.73-2.832l.02-.016zM3.653 2.026C6.073 3.06 8.69 4.941 10.8 7.258c2.46 2.7 4.109 5.828 4.637 9.149a.31.31 0 01-.421.335c-2.348-.945-4.54-1.258-6.59-1.02-1.739.2-3.337.792-4.816 1.703-.294.182-.62-.182-.405-.454 1.856-2.355 2.581-4.99 2.343-7.794-.195-2.292-1.031-4.61-2.284-6.709a.31.31 0 01.388-.442zM10.04 4.45c1.778.543 3.892 2.102 5.782 4.243 1.984 2.248 3.552 4.934 4.347 7.582a.31.31 0 01-.401.38l-.022-.01-.386-.154a10.594 10.594 0 00-.291-.112l-.016-.006c-.68-.247-1.199-.291-1.944-.101a.31.31 0 01-.375-.218C15.378 11.123 13.073 7.276 9.775 5c-.291-.201-.072-.653.266-.55zM4.273 2.996l.008.015c1.028 1.94 1.708 4.031 1.885 6.113.213 2.513-.31 4.906-1.673 7.092l-.02.031.003-.001c1.198-.581 2.47-.969 3.825-1.132l.055-.006c1.981-.23 4.083.029 6.309.837l.066.025-.007-.039c-.593-2.95-2.108-5.737-4.31-8.179l-.07-.078c-1.785-1.96-3.944-3.6-6.014-4.65l-.057-.028zm7.92 3.238l.048.048c2.237 2.295 3.885 5.431 4.974 9.191l.038.132.022-.004c.71-.133 1.284-.063 1.963.18l.027.01.066.024.046.018-.025-.073c-.811-2.307-2.208-4.62-3.936-6.594l-.058-.065c-1.02-1.155-2.103-2.132-3.15-2.856l-.015-.011z',
                    fillRule: 'evenodd',
                },
            ],
        },
    },
    runway: {
        id: 'runway',
        name: 'Runway',
        halo: '#6b7280',
        tile: { background: DARK_TILE, foreground: '#ffffff' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.6,
            paths: [
                {
                    d: 'M17.86 22.992c-2.669.245-4.887-2.876-6.597-4.454C10.398 24.759 1 24.177 1 17.86V6.15c0-.921.244-1.861.733-2.65C2.635 1.977 4.383.98 6.15 1h11.71c6.316 0 6.918 9.398.677 10.243l2.97 2.951c3.252 3.064.808 8.929-3.646 8.797zm-1.428-3.721c1.842 1.898 4.774-1.034 2.876-2.876l-5.132-5.132H11.3v2.876l4.436 4.436.696.696zM4.12 17.842c-.037 2.632 4.117 2.632 4.06 0V6.132c.038-1.316-1.353-2.35-2.612-1.955-.057.019-.113.037-.15.056-.79.301-1.335 1.09-1.317 1.936v11.673h.02zm13.74-9.68c2.632.037 2.632-4.098 0-4.06h-6.973c.526 1.109.395 2.857.413 4.06h6.56z',
                    fillRule: 'evenodd',
                },
            ],
        },
    },
    resemble: {
        id: 'resemble',
        name: 'Resemble AI',
        halo: '#1f6feb',
        tile: { background: '#1f6feb', foreground: '#ffffff' },
        monogram: 'R',
    },
    mmaudio: {
        id: 'mmaudio',
        name: 'MMAudio',
        halo: '#0f766e',
        tile: { background: '#0f766e', foreground: '#ffffff' },
        monogram: 'MM',
    },
    anthropic: {
        id: 'anthropic',
        name: 'Anthropic',
        halo: '#d97757',
        tile: { background: '#d97757', foreground: '#ffffff' },
        mark: {
            viewBox: '0 0 24 24',
            scale: 0.58,
            paths: [
                {
                    d: 'M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z',
                },
            ],
        },
    },
}

// Treechat's own creative flows and media utilities carry the house mark, whatever model runs underneath.
const TREECHAT_TOOL_TAGS = new Set([
    'illustrate',
    'film',
    'extend',
    'gif',
    'screenshot',
    'stitch',
    'resize',
    'pay',
    'treesearch',
])

// Ordered: the first matching rule wins. Tags are canonicalized (lowercase, no `!`,
// legacy `animate_`/`edit_` prefixes stripped) before matching.
const BRAND_RULES_BY_TAG: Array<[RegExp, AiModelBrandId]> = [
    [/^(gptimage|gpt4o|gpt|dalle|sora|openai)/, 'openai'],
    [/^(nb|gemini|flash|imagen)/, 'gemini'],
    [/^veo/, 'google'],
    [/^(grok|imagine)/, 'xai'],
    [/^seedance/, 'bytedance'],
    [/^kling/, 'kling'],
    [/^(h3|minimax|hailuo)/, 'minimax'],
    [/^flux/, 'bfl'],
    [/^(sd|stable)/, 'stability'],
    [/^(eleven|11)/, 'elevenlabs'],
    [/^suno/, 'suno'],
    [/^wan/, 'alibaba'],
    [/^qwen/, 'qwen'],
    [/^luma/, 'luma'],
    [/^midjourney/, 'midjourney'],
    [/^runway/, 'runway'],
    [/^(tts|clone|chatterbox)/, 'resemble'],
    [/^(video_sfx|mmaudio|foley|sfx)/, 'mmaudio'],
    [/^(promptcraft|claude|anthropic)/, 'anthropic'],
]

const BRAND_RULES_BY_NAME: Array<[RegExp, AiModelBrandId]> = [
    [/nano banana|gemini|imagen/, 'gemini'],
    [/\bveo\b/, 'google'],
    [/openai|\bgpt\b|gpt-image|dall|\bsora\b/, 'openai'],
    [/\bgrok\b|\bxai\b/, 'xai'],
    [/seedance|bytedance/, 'bytedance'],
    [/kling/, 'kling'],
    [/minimax|hailuo/, 'minimax'],
    [/\bflux\b|black forest/, 'bfl'],
    [/stable|stability/, 'stability'],
    [/eleven/, 'elevenlabs'],
    [/\bsuno\b/, 'suno'],
    [/\bqwen\b/, 'qwen'],
    [/\bwan\b|alibaba/, 'alibaba'],
    [/\bluma\b/, 'luma'],
    [/midjourney/, 'midjourney'],
    [/runway/, 'runway'],
    [/chatterbox|resemble/, 'resemble'],
    [/mmaudio/, 'mmaudio'],
    [/claude|anthropic|prompt craft/, 'anthropic'],
]

const BRAND_BY_PROVIDER: Record<string, AiModelBrandId> = {
    openai: 'openai',
    gemini: 'gemini',
    google: 'google',
    vertex: 'google',
    xai: 'xai',
    minimax: 'minimax',
    elevenlabs: 'elevenlabs',
    suno: 'suno',
    anthropic: 'anthropic',
    chatos: 'treechat',
}

const MONOGRAM_TILES: AiModelBrandTile[] = [
    { background: '#4f46e5', foreground: '#ffffff' },
    { background: '#0f766e', foreground: '#ffffff' },
    { background: '#b45309', foreground: '#ffffff' },
    { background: '#be185d', foreground: '#ffffff' },
    { background: '#1d4ed8', foreground: '#ffffff' },
    { background: '#065f46', foreground: '#ffffff' },
]

export function canonicalBrandTag(tag: string | null | undefined): string {
    const normalized = (tag || '').toString().trim().replace(/^!+/, '').toLowerCase()
    return normalized.replace(/^(animate|edit)_/, '')
}

function brandIdFor(subject: AiModelBrandSubject | null | undefined): AiModelBrandId | null {
    if (!subject) return null

    const tag = canonicalBrandTag(subject.commandTag || subject.actionTagName)
    if (subject.kind === 'tool' || TREECHAT_TOOL_TAGS.has(tag)) return 'treechat'

    if (tag) {
        const tagRule = BRAND_RULES_BY_TAG.find(([pattern]) => pattern.test(tag))
        if (tagRule) return tagRule[1]
    }

    const names = [subject.name, subject.humanName].filter(Boolean).join(' ').toLowerCase()
    if (names) {
        const nameRule = BRAND_RULES_BY_NAME.find(([pattern]) => pattern.test(names))
        if (nameRule) return nameRule[1]
    }

    const provider = (subject.provider || '').toString().trim().toLowerCase()
    return BRAND_BY_PROVIDER[provider] || null
}

export function aiModelBrandFor(
    subject: AiModelBrandSubject | null | undefined,
): AiModelBrand | null {
    const brandId = brandIdFor(subject)
    return brandId ? AI_MODEL_BRANDS[brandId] : null
}

export function aiModelBrandNameFor(
    subject: AiModelBrandSubject | null | undefined,
): string | null {
    return aiModelBrandFor(subject)?.name || null
}

// Monogram entries take their tile colour as the halo hue.
export function aiModelBrandHaloFor(
    subject: AiModelBrandSubject | null | undefined,
): AiModelBrandHalo {
    return aiModelBrandFor(subject)?.halo || aiModelMonogramTileFor(subject).background
}

// Letters for entries whose lab has no mark: initials of the first two words of the name.
export function aiModelMonogramFor(subject: AiModelBrandSubject | null | undefined): string {
    const source =
        subject?.humanName?.toString().trim() ||
        subject?.name?.toString().trim() ||
        canonicalBrandTag(subject?.actionTagName) ||
        ''
    const words = source.split(/[^a-z0-9]+/i).filter(Boolean)
    if (words.length === 0) return '?'
    return words
        .slice(0, 2)
        .map(word => word[0].toUpperCase())
        .join('')
}

export function aiModelMonogramTileFor(
    subject: AiModelBrandSubject | null | undefined,
): AiModelBrandTile {
    const seed =
        subject?.provider?.toString() ||
        subject?.humanName?.toString() ||
        subject?.name?.toString() ||
        canonicalBrandTag(subject?.actionTagName)
    let hash = 0
    for (const char of seed || '') hash = (hash * 31 + char.charCodeAt(0)) >>> 0
    return MONOGRAM_TILES[hash % MONOGRAM_TILES.length]
}
